import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Menu, X, Globe } from 'lucide-react';
// import i18n from '@/i18n';
import i18n from 'i18next';
import Cookies from 'js-cookie';
import { CHANGE_LANG } from 'context/actions/system';

// Хуудсын URL-г үүсгэх
const pageHref = (page) => {
    if (page.url) {
        return page.url.startsWith('/') ? page.url : '/' + page.url;
    }
    return `/content/${page.id}`;
};

// Хэлээс хамаарч нэр сонгох
// Англи болон Солонгос хувилбар хоосон бол Монгол руу автоматаар fallback хийнэ
const pickName = (page, lang) => {
    if (lang === 'ko' && page.nameKr) return page.nameKr;
    if (lang === 'en' && page.nameEn) return page.nameEn;
    return page.name || '';
};

// Хэлний харагдах label (хэрэглэгчдэд харагдах товчны бичиг)
const LANG_LABEL = { mn: 'МН', ko: 'KO', en: 'EN' };

const Header = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const dispatch = useDispatch();
    const userMenus = useSelector((s) => s.system.userMenus || []);
    const lang = useSelector((s) => s.system.lang || 'mn');

    const [scrolled, setScrolled] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileSubOpen, setMobileSubOpen] = useState(null);
    const [langOpen, setLangOpen] = useState(false);

    useEffect(() => {
        const isHome = router.pathname === '/';

        if (isHome) {
            // Нүүр хуудас — cover видео гарч ирсний дараа аажмаар гарна
            const timer = setTimeout(() => setMounted(true), 1100);
            const onScroll = () => setScrolled(window.scrollY > 20);
            onScroll();
            window.addEventListener('scroll', onScroll);
            return () => {
                clearTimeout(timer);
                window.removeEventListener('scroll', onScroll);
            };
        } else {
            // Бусад хуудас — шууд харагдана
            setMounted(true);
            const onScroll = () => setScrolled(window.scrollY > 20);
            onScroll();
            window.addEventListener('scroll', onScroll);
            return () => window.removeEventListener('scroll', onScroll);
        }
    }, [router.pathname]);

    useEffect(() => {
        const handleRouteChange = () => {
            setMobileOpen(false);
            setOpenDropdown(null);
            setLangOpen(false);
            setMobileSubOpen(null);
        };
        router.events.on('routeChangeStart', handleRouteChange);
        return () => router.events.off('routeChangeStart', handleRouteChange);
    }, [router.events]);

    // Хэл сонгогч — гадна дарахад хаах
    useEffect(() => {
        if (!langOpen) return;
        const handler = (e) => {
            if (!e.target.closest('[data-lang-dropdown]')) setLangOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [langOpen]);

    const changeLanguage = (l) => {
        i18n.changeLanguage(l);
        setLangOpen(false);
        // Redux шинэчлэх — pickName цэсийн орчуулга харна
        dispatch({ type: CHANGE_LANG, data: l });
        // Cookie-д хадгалах — дараагийн visit-д хэл санана
        Cookies.set('chosen_lang', l, { expires: 365 });
    };

    const headerBg = scrolled ? 'bg-white shadow-md' : 'bg-black/35 backdrop-blur-sm';
    const textColor = scrolled ? 'text-brand-navy' : 'text-white';
    const textHover = scrolled ? 'hover:bg-black/5' : 'hover:bg-white/10';
    const subtitleColor = scrolled ? 'text-brand-red' : 'text-brand-yellow';

    return (
        <>
            <header
                className={`fixed top-0 left-0 right-0 z-50 ${headerBg}`}
                style={{
                    transform: mounted ? 'translateY(0)' : 'translateY(-100%)',
                    opacity: mounted ? 1 : 0,
                    transition:
                        mounted && router.pathname === '/'
                            ? 'transform 1.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 1.4s ease'
                            : 'none',
                }}
            >
                <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
                    <div className="flex items-center justify-between h-20 lg:h-24">
                        {/* Logo */}
                        <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
                            <Image
                                src="/logo.png"
                                alt="Хүрээ Тулга"
                                width={44}
                                height={44}
                                className="object-contain lg:w-[56px] lg:h-[56px]"
                                priority
                            />
                            <div className="block">
                                <div
                                    className={`${textColor} font-bold text-[13px] lg:text-[14px] leading-tight transition-colors`}
                                >
                                    ХҮРЭЭ ТУЛГА
                                </div>
                                <div
                                    className={`${subtitleColor} text-[9px] lg:text-[10px] tracking-wider transition-colors`}
                                >
                                    {t('school.name_full')}
                                </div>
                            </div>
                        </Link>

                        {/* Desktop Menu */}
                        <nav className="hidden lg:flex items-center gap-0.5">
                            {/* Dynamic pages */}
                            {userMenus.map((page) => {
                                const hasChildren = page.children?.length > 0;
                                const isOpen = openDropdown === page.id;
                                const label = pickName(page, lang);

                                return (
                                    <div
                                        key={page.id}
                                        className="relative"
                                        onMouseEnter={() => hasChildren && setOpenDropdown(page.id)}
                                        onMouseLeave={() => setOpenDropdown(null)}
                                    >
                                        {hasChildren ? (
                                            <>
                                                <button
                                                    className={`flex items-center gap-0.5 px-2 py-2 text-[12px] font-semibold uppercase transition ${
                                                        isOpen
                                                            ? 'bg-brand-yellow text-brand-navy'
                                                            : `${textColor} ${textHover}`
                                                    }`}
                                                >
                                                    {label}
                                                    <ChevronDown
                                                        size={11}
                                                        className={`transition-transform flex-shrink-0 ${
                                                            isOpen ? 'rotate-180' : ''
                                                        }`}
                                                    />
                                                </button>
                                                {isOpen && (
                                                    <div className="absolute top-full left-0 min-w-[220px] bg-white shadow-xl border-t-2 border-brand-yellow">
                                                        <ul className="py-2">
                                                            {page.children.map((child) => (
                                                                <li key={child.id}>
                                                                    <Link
                                                                        href={pageHref(child)}
                                                                        className="block px-5 py-3 text-[13px] text-brand-navy hover:bg-gray-50 hover:text-brand-red transition"
                                                                    >
                                                                        {pickName(child, lang)}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <Link
                                                href={pageHref(page)}
                                                className={`block px-2 py-2 text-[12px] font-semibold uppercase transition ${textColor} ${textHover}`}
                                            >
                                                {label}
                                            </Link>
                                        )}
                                    </div>
                                );
                            })}
                        </nav>

                        {/* Language switcher */}
                        <div className="relative hidden lg:block" data-lang-dropdown>
                            <button
                                onClick={() => setLangOpen(!langOpen)}
                                className={`flex items-center gap-2 px-3 py-2 text-[12px] font-medium rounded transition ${textColor} ${textHover}`}
                            >
                                <Globe size={16} />
                                {LANG_LABEL[i18n.language] || 'МН'}
                                <ChevronDown size={12} />
                            </button>
                            {langOpen && (
                                <div className="absolute top-full right-0 min-w-[120px] bg-white shadow-xl border border-gray-100 mt-1">
                                    <button
                                        onClick={() => changeLanguage('mn')}
                                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                                            lang === 'mn'
                                                ? 'text-brand-yellow font-bold'
                                                : 'text-brand-navy'
                                        }`}
                                    >
                                        Монгол
                                    </button>
                                    <button
                                        onClick={() => changeLanguage('ko')}
                                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                                            lang === 'ko'
                                                ? 'text-brand-yellow font-bold'
                                                : 'text-brand-navy'
                                        }`}
                                    >
                                        한국어
                                    </button>
                                    <button
                                        onClick={() => changeLanguage('en')}
                                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                                            lang === 'en'
                                                ? 'text-brand-yellow font-bold'
                                                : 'text-brand-navy'
                                        }`}
                                    >
                                        English
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Mobile: hamburger */}
                        <button
                            onClick={() => setMobileOpen(!mobileOpen)}
                            className={`lg:hidden p-2 ${textColor}`}
                            aria-label="Menu"
                        >
                            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer — header-ийн гадна, document.body-д render */}
            {mounted &&
                createPortal(
                    <>
                        {/* Backdrop */}
                        <div
                            className={`lg:hidden fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 ${
                                mobileOpen
                                    ? 'opacity-100 pointer-events-auto'
                                    : 'opacity-0 pointer-events-none'
                            }`}
                            onClick={() => setMobileOpen(false)}
                        />

                        {/* Drawer panel */}
                        <div
                            className={`lg:hidden fixed top-0 right-0 z-[70] h-full w-full bg-brand-navy flex flex-col transition-transform duration-300 ease-in-out ${
                                mobileOpen ? 'translate-x-0' : 'translate-x-full'
                            }`}
                        >
                            {/* Drawer header */}
                            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => changeLanguage('mn')}
                                        className={`px-4 py-1.5 text-sm font-bold rounded transition ${
                                            lang === 'mn'
                                                ? 'bg-brand-yellow text-brand-navy'
                                                : 'text-white/60 hover:text-white border border-white/20'
                                        }`}
                                    >
                                        МН
                                    </button>
                                    <button
                                        onClick={() => changeLanguage('ko')}
                                        className={`px-4 py-1.5 text-sm font-bold rounded transition ${
                                            lang === 'ko'
                                                ? 'bg-brand-yellow text-brand-navy'
                                                : 'text-white/60 hover:text-white border border-white/20'
                                        }`}
                                    >
                                        KO
                                    </button>
                                    <button
                                        onClick={() => changeLanguage('en')}
                                        className={`px-4 py-1.5 text-sm font-bold rounded transition ${
                                            lang === 'en'
                                                ? 'bg-brand-yellow text-brand-navy'
                                                : 'text-white/60 hover:text-white border border-white/20'
                                        }`}
                                    >
                                        EN
                                    </button>
                                </div>
                                <button
                                    onClick={() => setMobileOpen(false)}
                                    className="p-2 text-white/70 hover:text-white transition"
                                    aria-label="Хаах"
                                >
                                    <X size={26} />
                                </button>
                            </div>

                            {/* Цэс */}
                            <nav className="flex-1 overflow-y-auto px-6 py-4">
                                {userMenus.map((page) => {
                                    const hasChildren = page.children?.length > 0;
                                    const isOpen = mobileSubOpen === page.id;
                                    const label = pickName(page, lang);

                                    return (
                                        <div
                                            key={page.id}
                                            className="border-b border-white/10 last:border-0"
                                        >
                                            {hasChildren ? (
                                                <>
                                                    <button
                                                        onClick={() =>
                                                            setMobileSubOpen(
                                                                isOpen ? null : page.id
                                                            )
                                                        }
                                                        className="flex items-center justify-between w-full py-4 text-sm font-bold uppercase tracking-wider text-white hover:text-brand-yellow transition"
                                                    >
                                                        {label}
                                                        <ChevronDown
                                                            size={18}
                                                            className={`transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                                                        />
                                                    </button>
                                                    {isOpen && (
                                                        <ul className="pb-3 pl-3 space-y-1">
                                                            {page.children.map((child) => (
                                                                <li key={child.id}>
                                                                    <Link
                                                                        href={pageHref(child)}
                                                                        className="flex items-center gap-2 py-2.5 text-sm text-white/70 hover:text-brand-yellow transition"
                                                                    >
                                                                        <span className="w-1 h-1 rounded-full bg-brand-yellow flex-shrink-0" />
                                                                        {pickName(child, lang)}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </>
                                            ) : (
                                                <Link
                                                    href={pageHref(page)}
                                                    className="flex items-center py-4 text-sm font-bold uppercase tracking-wider text-white hover:text-brand-yellow transition"
                                                >
                                                    {label}
                                                </Link>
                                            )}
                                        </div>
                                    );
                                })}
                            </nav>

                            {/* Drawer footer */}
                            <div className="px-6 py-5 border-t border-white/10 flex-shrink-0">
                                <p className="text-white/30 text-xs text-center tracking-widest uppercase">
                                    ХҮРЭЭ ТУЛГА
                                </p>
                            </div>
                        </div>
                    </>,
                    document.body
                )}
        </>
    );
};

export default Header;